import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import {
    IApplicationDTO,
    ICharacteristics,
    IContactInformation,
    IExaminerInformation,
    IPersonalInformation,
    IPool,
    IPoolApplicationDTO,
    IReferences,
    ISkills,
    IStandardizedRoles,
    IUploadAvatar,
    IUploadCoverLatter,
    IUploadDocs,
    IUserApplicationState,
    IWorkHistory
} from './application.interface';
import { inject, Injectable, input } from '@angular/core';
import { ApplicationService } from './application.service';
import {
    AddApplicationReference,
    AddApplicationToPool,
    ApplicationDepartment,
    CancelApplication,
    ClearApplication,
    CreateApplication,
    DeleteApplication,
    DeletReference,
    GetAllApplications,
    GetApplicationReferences,
    GetOneApplication,
    GetPoolApplications,
    GetSingleApplication,
    GetUserApplications,
    ReviewApplicationStatus,
    SaveApplicationNote,
    SaveReferenceNote,
    SetApplicationPreview,
    SubmitApplication,
    UpdateApplication,
    UpdateApplicationReference,
    UpdateApplicationStatus,
    UploadAvatar,
    UploadCoverLater,
    UploadResume,
    UserApplication,
    SendHiringWorkflowEmail,
    PreviewHiringWorkflowEmail
} from './application.action';
import { catchError, tap } from 'rxjs';

export class IApplicationProps {
    applications!: IApplicationDTO[];
    poolApplications!: IPoolApplicationDTO[];
    application!: IApplicationDTO | undefined;
    singleAppl!: IApplicationDTO | undefined;
    references!: IReferences[];
    reference!: IReferences | undefined;
    userApplication: IUserApplicationState | undefined;
    resume!: IUploadDocs | undefined;
    avatar!: IUploadAvatar | undefined;
    coverLeter: IUploadCoverLatter | undefined;
    loading!: boolean;
    isPreview!: boolean;
    loadingSingle!: boolean;
    error!: string | null;
}

@State<IApplicationProps>({
    name: 'application',
    defaults: {
        applications: [],
        poolApplications: [],
        references: [],
        reference: undefined,
        application: undefined,
        singleAppl: undefined,
        resume: undefined,
        avatar: undefined,
        coverLeter: undefined,
        userApplication: undefined,
        loading: false,
        isPreview: false,
        loadingSingle: false,
        error: null
    }
})
@Injectable()
export class ApplicationState {
    applicationService: ApplicationService = inject(ApplicationService);
    private store: Store = inject(Store);

    @Selector()
    static getApplications(state: IApplicationProps) {
        return state.applications;
    }

    @Selector()
    static getApplication(state: IApplicationProps) {
        return state.application;
    }

    @Selector()
    static getSingle(state: IApplicationProps) {
        return state.singleAppl;
    }

    @Selector()
    static loading(state: IApplicationProps) {
        return state.loading;
    }

    @Selector()
    static loadinSingle(state: IApplicationProps) {
        return state.loadingSingle;
    }

    @Selector()
    static getUserApplication(state: IApplicationProps) {
        return state.userApplication;
    }

    @Selector()
    static getReferences(state: IApplicationProps) {
        return state.references;
    }

    @Selector()
    static getAvatar(state: IApplicationProps) {
        return state.avatar;
    }

    @Selector()
    static getResume(state: IApplicationProps) {
        return state.resume;
    }

    @Selector()
    static isPreview(state: IApplicationProps): boolean {
        return state.isPreview;
    }

    @Selector()
    static getCoverLetter(state: IApplicationProps) {
        return state.coverLeter;
    }

    @Selector()
    static getPoolsApplication(state: IApplicationProps) {
        return state.poolApplications;
    }

    @Action(UserApplication)
    examApplication(ctx: StateContext<IApplicationProps>, action: UserApplication) {
        const state = ctx.getState();

        // Clear state if userId is undefined
        if (!action.payload?.userId) {
            ctx.patchState({ userApplication: undefined });
            return;
        }

        const data = action.payload;
        const dataWithUser = data as any; // Cast to access user field

        const isFinished = (field: any) => !!field;

        const examData: IUserApplicationState = {
            id: data.id ?? null,
            requiredField: {
                department: data.department,
                applicationStatus: data?.applicationStatus,
                jobId: data.jobId,
                userId: data.userId,
                isFinished: isFinished(data.jobId)
            },
            // Preserve user data if it exists (for admin-created applications)
            ...(dataWithUser.user && { user: dataWithUser.user }),
            dynamicFields: {
                linkedInUrl: data?.linkedInUrl,
                address: data?.address,
                coverLetter: data?.coverLetter,
                dateAvailable: data?.dateAvailable,
                desiredSalary: data?.desiredSalary,
                referredBy: data?.referredBy,
                heighestEducation: data?.heighestEducation,
                twitterUserName: data?.twitterUserName,
                university: data?.university,
                website: data?.website,

                isFinished: isFinished(data.id)
            },
            personalInformation: {
                ...data.personalInformation,
                isFinished: isFinished(data.personalInformation?.gender)
            },
            contactInformation: {
                ...data.contactInformation,
                isFinished: isFinished(data.contactInformation?.mobilePhone)
            },
            skills: {
                ...data.skills,
                isFinished: isFinished(data.skills?.instantMessagingTools)
            },
            workHistory: {
                ...data.workHistory,
                isFinished: isFinished(data.workHistory?.inPerson)
            },
            // examinerInformation: {
            //     ...data.examinerInformation,
            //     isFinished: isFinished(data.examinerInformation?.examinerType)
            // },
            characteristics: {
                ...data.characteristics,
                isFinished: isFinished(data.characteristics?.accent)
            },
            standardizedRoles: {
                ...data.standardizedRoles,
                isFinished: isFinished(data.standardizedRoles?.intro)
            },
            references: {
                references: data.references,
                isFinished: true

                // isFinished: true,
            }
        };
        ctx.patchState({
            userApplication: examData
        });
    }

    @Action(ApplicationDepartment)
    setApplicationDepartment(ctx: StateContext<IApplicationProps>, action: ApplicationDepartment) {
        // Get the current state
        const state = ctx.getState();

        // Construct the updated user application state
        const updatedUserApplication: IUserApplicationState = {
            ...state.userApplication,
            requiredField: {
                department: action.input.department,
                isFinished: true
            },

            id: state.userApplication?.id ?? null
        };

        ctx.patchState({
            userApplication: updatedUserApplication
        });
    }

    @Action(ClearApplication)
    clearApplication(ctx: StateContext<IApplicationProps>, action: ClearApplication) {
        const state = ctx.getState();
        ctx.patchState({
            userApplication: undefined,
            references: [],  // Clear references to prevent showing stale data
            reference: undefined
        });
    }

    @Action(SetApplicationPreview)
    setApplicationPreview(ctx: StateContext<IApplicationProps>, action: SetApplicationPreview) {
        // const state = ctx.getState();
        ctx.patchState({ isPreview: action.payload });
    }

    @Action(GetAllApplications)
    get(ctx: StateContext<IApplicationProps>, action: GetAllApplications) {
        const state = ctx.getState();
        ctx.patchState({ loading: true });

        return this.applicationService.getApplications(action.queryParams).pipe(
            tap((res) => {
                ctx.patchState({
                    applications: res as IApplicationDTO[],
                    loading: false,
                    error: null
                });
            }),
            catchError((err) => {
                ctx.patchState({
                    loading: false,
                    error: err.message
                });
                throw err;
            })
        );
    }

    @Action(GetPoolApplications)
    getPoolApp(ctx: StateContext<IApplicationProps>, action: GetPoolApplications) {
        const state = ctx.getState();

        ctx.patchState({ loading: true });
        return this.applicationService.getPoolAppl(action.department).pipe(
            tap((res) => {
                ctx.patchState({
                    poolApplications: res as IPoolApplicationDTO[],
                    loading: false
                });
            }),
            catchError((err) => {
                ctx.patchState({
                    loading: false
                });
                throw err;
            })
        );
    }

    @Action(GetUserApplications)
    getUserAppl(ctx: StateContext<IApplicationProps>, action: GetUserApplications) {
        const state = ctx.getState();
        ctx.patchState({ loading: true });

        return this.applicationService.getUserApplications(action.id).pipe(
            tap((res) => {
                ctx.patchState({
                    applications: res as IApplicationDTO[],
                    loading: false,
                    error: null
                });
            }),
            catchError((err) => {
                ctx.patchState({
                    loading: false,
                    error: err.message
                });
                throw err;
            })
        );
    }

    @Action(GetSingleApplication)
    getSingle(ctx: StateContext<IApplicationProps>, action: GetSingleApplication) {
        const state = ctx.getState();
        ctx.patchState({ loading: true });

        return this.applicationService.getSingle(action.id).pipe(
            tap((res) => {
                ctx.patchState({
                    application: res as IApplicationDTO,
                    loading: false
                });
            }),
            catchError((err) => {
                ctx.patchState({
                    loading: false,
                    error: err.message
                });
                throw err;
            })
        );
    }

    @Action(GetOneApplication)
    getOne(ctx: StateContext<IApplicationProps>, action: GetOneApplication) {
        const state = ctx.getState();
        ctx.patchState({ loadingSingle: true });

        return this.applicationService.getSingle(action.id).pipe(
            tap((res) => {
                ctx.patchState({
                    singleAppl: res as IApplicationDTO,
                    loadingSingle: false
                });
            }),
            catchError((err) => {
                ctx.patchState({
                    loadingSingle: false,
                    error: err.message
                });
                throw err;
            })
        );
    }

    @Action(CreateApplication)
    create(ctx: StateContext<IApplicationProps>, action: CreateApplication) {
        const state = ctx.getState();
        ctx.patchState({ loading: true });

        return this.applicationService.createApplication(action.input).pipe(
            tap((response) => {
                ctx.patchState({
                    applications: [...state.applications, response as IApplicationDTO],
                    application: response as IApplicationDTO,
                    loading: false,
                    error: null
                });
                this.store.dispatch(new UserApplication(response as IApplicationDTO));
            }),
            catchError((error) => {
                ctx.patchState({
                    loading: false,
                    error: error.message
                });
                throw error;
            })
        );
    }

    @Action(GetApplicationReferences)
    getReferences(ctx: StateContext<IApplicationProps>, action: GetApplicationReferences) {
        const state = ctx.getState();

        // Clear old references immediately to prevent flash of stale data
        ctx.patchState({ loading: true, references: [] });
        return this.applicationService.getApplicationReference(action.id).pipe(
            tap((res: any) => {
                ctx.patchState({
                    references: res?.data?.references,
                    loading: false
                });
            }),
            catchError((err) => {
                ctx.patchState({
                    loading: false
                });
                throw err;
            })
        );
    }

    @Action(AddApplicationReference)
    createReference(ctx: StateContext<IApplicationProps>, action: AddApplicationReference) {
        const state = ctx.getState();
        ctx.patchState({
            loading: true
        });

        return this.applicationService.addReference(action.input).pipe(
            tap((res) => {
                ctx.patchState({
                    references: [...state.references, res as IReferences],
                    reference: res as IReferences,

                    loading: false
                });
            }),
            catchError((error) => {
                ctx.patchState({
                    loading: false,
                    error: error.message
                });
                throw error;
            })
        );
    }

    @Action(DeletReference)
    deleteReference(ctx: StateContext<IApplicationProps>, action: DeletReference) {
        const state = ctx.getState();
        return this.applicationService.deleteReference(action.id).pipe(
            tap((res) => {
                ctx.patchState({
                    references: state.references.filter((ref) => ref.id !== action.id)
                });
            }),
            catchError((err) => {
                ctx.patchState({
                    error: err.message
                });
                throw err;
            })
        );
    }

    @Action(UpdateApplicationReference)
    updateReference(ctx: StateContext<IApplicationProps>, action: UpdateApplicationReference) {
        const state = ctx.getState();

        return this.applicationService.updateReference(action.input).pipe(
            tap((res: any) => {
                ctx.patchState({
                    references: state.references.map((ref) =>
                        ref.id === action.input.id ? { ...ref, res, note: res?.note } : ref
                    )
                });
            }),
            catchError((error) => {
                ctx.patchState({
                    error: error.message
                });
                throw error;
            })
        );
    }

    @Action(UpdateApplication)
    updateJob(ctx: StateContext<IApplicationProps>, action: UpdateApplication) {
        const state = ctx.getState();
        ctx.patchState({ loading: true });
        return this.applicationService.updateApplication(action.input).pipe(
            tap((res) => {
                ctx.patchState({
                    applications: state.applications.map((appl) =>
                        appl.id === action.input.id ? { ...appl, res } : appl
                    ),
                    application: res as IApplicationDTO,
                    loading: false,
                    error: null
                });
                this.store.dispatch(new UserApplication(res as IApplicationDTO));
            }),
            catchError((err) => {
                ctx.patchState({
                    loading: false,
                    error: err.message
                });
                throw err;
            })
        );
    }

    @Action(DeleteApplication)
    deleteJob(ctx: StateContext<IApplicationProps>, action: DeleteApplication) {
        const state = ctx.getState();
        ctx.patchState({ loading: true });
        return this.applicationService.delete(action.id).pipe(
            tap((res) => {
                ctx.patchState({
                    applications: state.applications.filter((appl) => appl.id !== action.id),
                    loading: false,
                    error: null
                });
            }),
            catchError((err) => {
                ctx.patchState({
                    loading: false,
                    error: err.message
                });
                throw err;
            })
        );
    }

    @Action(CancelApplication)
    cancelApplication(ctx: StateContext<IApplicationProps>, action: CancelApplication) {
        const state = ctx.getState();
        ctx.patchState({ loading: true });
        return this.applicationService.cancelApplication(action.id).pipe(
            tap((res) => {
                ctx.patchState({
                    applications: state.applications.map((appl) =>
                        appl.id === action.id ? { ...appl, applicationStatus: 'Cancelled' } : appl
                    ),
                    loading: false,
                    error: null
                });
            }),
            catchError((err) => {
                ctx.patchState({
                    loading: false,
                    error: err.message
                });
                throw err;
            })
        );
    }

    @Action(AddApplicationToPool)
    addToPool(ctx: StateContext<IApplicationProps>, action: AddApplicationToPool) {
        const state = ctx.getState();

        return this.applicationService.addToPool(action.input).pipe(
            tap((res: any) => {
                ctx.patchState({
                    applications: state.applications.map((appl) =>
                        appl.id === action.input.id ? { ...appl, pool: res.pool } : appl
                    )
                });
            }),
            catchError((err) => {
                ctx.patchState({
                    loading: false,
                    error: err.message
                });
                throw err;
            })
        );
    }

    @Action(ReviewApplicationStatus)
    review(ctx: StateContext<IApplicationProps>, action: ReviewApplicationStatus) {
        const state = ctx.getState();
        ctx.patchState({
            loading: true
        });
        return this.applicationService.reviewStatus(action.input).pipe(
            tap((res: any) => {
                ctx.patchState({
                    applications: state.applications.map((appl) =>
                        appl.id === action.input.id
                            ? {
                                  ...appl,
                                  applicationReviewStatus: res?.applicationReviewStatus
                              }
                            : appl
                    ),
                    loading: false
                });
            }),
            catchError((err) => {
                ctx.patchState({
                    loading: false
                });
                throw err;
            })
        );
    }

    @Action(SubmitApplication)
    submit(ctx: StateContext<IApplicationProps>, action: SubmitApplication) {
        const state = ctx.getState();
        ctx.patchState({ loading: true });
        return this.applicationService.submitApplication(action.input).pipe(
            tap((res) => {
                ctx.patchState({
                    loading: false
                });
                this.store.dispatch(new UserApplication(res as IApplicationDTO));
            }),
            catchError((err) => {
                ctx.patchState({
                    loading: false
                });
                throw err;
            })
        );
    }

    @Action(UploadAvatar)
    avatarUpload(ctx: StateContext<IApplicationProps>, action: UploadAvatar) {
        return this.applicationService.uploadAvatar(action.input).pipe(
            tap((res) => {
                ctx.patchState({
                    avatar: res as IUploadAvatar
                });
            }),
            catchError((err) => {
                throw err;
            })
        );
    }

    @Action(UploadResume)
    avatarResume(ctx: StateContext<IApplicationProps>, action: UploadResume) {
        return this.applicationService.uploadResume(action.input).pipe(
            tap((res) => {
                ctx.patchState({
                    resume: res as IUploadDocs
                });
            }),
            catchError((err) => {
                throw err;
            })
        );
    }

    @Action(UploadCoverLater)
    avatarCoverLetter(ctx: StateContext<IApplicationProps>, action: UploadCoverLater) {
        return this.applicationService.uploadCoverLetter(action.input).pipe(
            tap((res) => {
                ctx.patchState({
                    coverLeter: res as IUploadCoverLatter
                });
            }),
            catchError((err) => {
                throw err;
            })
        );
    }

    @Action(SaveApplicationNote)
    addApplicationNote(ctx: StateContext<IApplicationProps>, action: SaveApplicationNote) {
        return this.applicationService.applicationNote(action.payload).pipe(
            tap((res: IApplicationDTO) => {
                // console.log('Saved Application note', res);
                const state = ctx.getState();
                this.store.dispatch(new UserApplication(res as IApplicationDTO));
                // const updatedUserApplication = {
                //   ...state.userApplication!,
                //   [action.payload.step]: res
                // };
                // console.log('Updated USer', updatedUserApplication);
                // ctx.patchState({
                //   // coverLeter: res as IUploadCoverLatter
                //   // userApplication: updatedUserApplication
                // });
            }),
            catchError((err) => {
                throw err;
            })
        );
    }

    @Action(SaveReferenceNote)
    saveReferenceNote(ctx: StateContext<IApplicationProps>, action: SaveReferenceNote) {
        return this.applicationService.referenceNote(action.payload).pipe(
            tap((res: IReferences) => {
                console.log('Saved Reference Note', res);
                // "6821f5833933e7fc872c5cf6"
                const state = ctx.getState();
                ctx.patchState({
                    references: state.references.map((ref) =>
                        ref.id === res.id ? { ...ref, ...res } : ref
                    )
                });
            }),
            catchError((err) => {
                throw err;
            })
        );
    }

    @Action(SendHiringWorkflowEmail)
    sendHiringWorkflowEmail(ctx: StateContext<IApplicationProps>, action: SendHiringWorkflowEmail) {
        const state = ctx.getState();
        ctx.patchState({ loading: true });
        return this.applicationService.sendHiringWorkflowEmail(action.input).pipe(
            tap((res: any) => {
                ctx.patchState({ loading: false });
            }),
            catchError((err) => {
                ctx.patchState({ loading: false });
                throw err;
            })
        );
    }

    @Action(PreviewHiringWorkflowEmail)
    previewHiringWorkflowEmail(ctx: StateContext<IApplicationProps>, action: PreviewHiringWorkflowEmail) {
        return this.applicationService.previewHiringWorkflowEmail(action.input);
    }

    @Action(UpdateApplicationStatus)
    updateApplicationStatus(ctx: StateContext<IApplicationProps>, action: UpdateApplicationStatus) {
        const state = ctx.getState();
        ctx.patchState({ loading: true });
        return this.applicationService.updateApplicationStatus(action.payload).pipe(
            tap((updatedApplication: IApplicationDTO) => {
                ctx.patchState({
                    applications: state.applications.map(app =>
                        app.id === action.payload.id ? updatedApplication : app
                    ),
                    loading: false,
                    error: null
                });
            }),
            catchError((err) => {
                ctx.patchState({
                    loading: false,
                    error: err.message
                });
                throw err;
            })
        );
    }
}
